import React from 'react';
import MultiClamp from 'react-multi-clamp';
import Text from 'crehana-ui/Text';
import PrimaryButton from 'crehana-ui/Buttons/PrimaryButton';
import Crehanaicons180 from 'crehana-icons/Crehanaicons180';
import { white, baseMain } from 'crehui/styles/colors';
import mixpanel from 'Jsx/Utils/mixpanelTracking';
import { useMe } from 'Jsx/ContextApis/AuthContext';
import Tag from './Tag';
import formatDate from '../utils';
import { translations } from '../locales/translations';

const ContentPreview = ({
  originalId,
  title,
  preSale,
  courseTag,
  launchDate,
  enrollmentUrl,
  urlToBuyNow,
  canEnroll,
  professor: { fullName: professor },
  level: { name: level },
  url,
  prices: { currencySymbol, discount, price, realPrice },
  isEnrollment,
  courseType,
  showButton,
  showLevel,
  showAccessPreview,
  enrollmentAccessPreviewUrl,
  userLanguage,
  dark,
  hidePrices,
}) => {
  const languageKey = userLanguage === 'pt' ? translations.pt : translations.es;
  const { me } = useMe();

  const handleButtonRedirection = ({ redirectUrl, track }) => {
    mixpanel.track(track, {
      course: originalId,
      title,
    });

    setTimeout(() => {
      window.location.href = redirectUrl;
    }, 0);
  };

  const renderButtonData = () => {
    if (canEnroll) {
      if (courseType === 'temporal-access') {
        return {
          label: languageKey.CAN_ENROLL.TEMPORAL_ACCESS,
          track: mixpanel.EVENTS.ENROLLMENT_TO_COURSE_FROM_COURSE_PREVIEW,
          url: enrollmentUrl,
        };
      }
      if (courseType === 'freemium' || showAccessPreview) {
        return {
          label: languageKey.CAN_ENROLL.FREEMIUM,
          track: mixpanel.EVENTS.ENROLLMENT_TO_COURSE_FROM_COURSE_PREVIEW,
          url: enrollmentAccessPreviewUrl,
        };
      }
      if (courseType === 'membership') {
        return {
          label: languageKey.CAN_ENROLL.MEMBERSHIP,
          track: mixpanel.EVENTS.ENROLLMENT_TO_COURSE_FROM_COURSE_PREVIEW,
          url: enrollmentUrl,
        };
      }
      if (courseType === 'free') {
        return {
          label: languageKey.CAN_ENROLL.FREE,
          track: mixpanel.EVENTS.ENROLLMENT_TO_COURSE_FROM_COURSE_PREVIEW,
          url: enrollmentUrl,
        };
      }
      return {
        label: languageKey.CAN_ENROLL.DEFAULT,
        track: mixpanel.EVENTS.ENROLLMENT_TO_COURSE_FROM_COURSE_PREVIEW,
        url: enrollmentUrl,
      };
    }
    if (courseType === 'membership') {
      return {
        label: languageKey.CANNOT_ENROLL.MEMBERSHIP,
        track: mixpanel.EVENTS.GO_TO_MEMBERSHIP_LANDING,
        url: '/membresias/',
      };
    }
    if (courseType === 'paid') {
      return {
        label: languageKey.CANNOT_ENROLL.PAID,
        track: mixpanel.EVENTS.BUY_NOW_FROM_COURSE_PREVIEW,
        url: urlToBuyNow,
      };
    }
    if (isEnrollment) {
      return {
        label: languageKey.CANNOT_ENROLL.DEFAULT,
        track: mixpanel.EVENTS.GO_TO_COURSE_FROM_COURSE_PREVIEW,
        url,
      };
    }
    return {
      label: languageKey.CANNOT_ENROLL.PAID,
      track: mixpanel.EVENTS.BUY_NOW_FROM_COURSE_PREVIEW,
      url: urlToBuyNow,
    };
  };

  const hasDiscount = discount > 0;

  const getPriceColor = () => {
    if (hasDiscount) {
      return dark ? 'secondary-main' : 'red-main';
    }
    return dark ? 'white' : 'base-main';
  };

  return (
    <div className="flex flex-col" style={{ width: 520, maxHeight: 186 }}>
      <div
        className={`p-16 w-full flex justify-between ${
          dark ? 'bg-base-main' : 'bg-white'
        }`}
        style={{ maxHeight: 186 }}
      >
        <div
          className={`flex flex-col items-start ${
            showButton && !me?.hasOrganization ? 'w-1/2 pr-16' : 'w-full'
          }`}
        >
          {courseTag && (
            <div className="flex justify-start items-center mb-12">
              <Tag
                textLabel={courseTag && courseTag.textLabel}
                textColor={courseTag && courseTag.textColor}
                backgroundColor={courseTag && courseTag.backgroundColor}
              />
              {preSale && launchDate && (
                <div>
                  <Crehanaicons180
                    size={10}
                    color={dark ? white : baseMain}
                    className="mt-0 mr-6 mb-0 ml-12"
                  />
                  <Text type="body4" color={dark ? 'white' : 'base-main'}>
                    {formatDate(launchDate)}
                  </Text>
                </div>
              )}
            </div>
          )}
          <a href={url} className="mb-8">
            <Text
              type="subtitle2"
              color={dark ? 'white' : 'base-main'}
              className="block hover:text-secondary-main"
            >
              <MultiClamp ellipsis="..." clamp={2} disableCssClamp>
                {title}
              </MultiClamp>
            </Text>
          </a>
          <Text
            as="p"
            type="subtitle4"
            color={dark ? 'white' : 'base-main'}
            className="mb-16"
          >
            {professor}
          </Text>
          {showLevel && (
            <Text color="secondary-main" type="body4">
              {`Nivel ${level}`}
            </Text>
          )}
        </div>
        {showButton && !me?.hasOrganization && !hidePrices && (
          <div className="flex flex-col justify-start items-end w-1/2">
            <PrimaryButton
              onClick={() => {
                handleButtonRedirection({
                  track: renderButtonData()?.track,
                  redirectUrl: renderButtonData()?.url,
                });
              }}
              label={renderButtonData()?.label}
              darkMode={dark}
            />
            {!canEnroll && !me?.isPremiumMember && (
              <div className="flex justify-end items-center mt-12 w-full">
                <div className="mr-8 flex items-end">
                  <Text
                    color={getPriceColor()}
                    responsive="sm"
                    type="currency-s"
                  >
                    {currencySymbol}
                  </Text>
                  <Text color={getPriceColor()} responsive="sm" type="price-s">
                    {price}
                  </Text>
                </div>
                {realPrice && discount ? (
                  <Text
                    color={dark ? 'white' : 'base-main'}
                    responsive="sm"
                    type="discount-s"
                    className="mr-8 line-through"
                  >
                    {`${currencySymbol}${realPrice}`}
                  </Text>
                ) : null}
                {realPrice && discount ? (
                  <Text
                    type="subtitle4"
                    color={dark ? 'white' : 'base-main'}
                  >{`${discount}% Dto.`}</Text>
                ) : null}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ContentPreview;
